/**
 * GovRAMP template pins + header-label manifests — V103-7-2 / V103-7-3.
 *
 * Templates are NOT in the repo or the bundle: the GovRAMP package has no
 * stated redistribution terms (docs/v10.3/xlsx-spike.md, Risks). Each one is
 * stored in R2 under its sha256 (`govrampTemplateObjectKey`) by an operator,
 * or uploaded by a customer from their own govramp.org download; an upload is
 * accepted only when its sha256 is pinned here.
 *
 * Every write binds to a HEADER LABEL (or a form label), never a column
 * letter. The labels below were read from the Moderate Controls Matrix V1.04
 * and V1.06 (identical layouts) and the ConMon matrix V1.2.
 */
export const GOVRAMP_TEMPLATE_PINS = Object.freeze([
    {
        key: 'govramp-ocm-moderate', version: 'V1.06', current: true, bytes: 458_436,
        sha256: '787c801f7dc593da33ece573cc8e9abf2179d98283f4592fa5bf85586e7eba49',
        filename: 'GovRAMP-Controls-Matrix_Mod_Rev5_V1.06.xlsx',
        source: 'https://govramp.org/hubfs/GovRAMP-Controls-Matrix_Mod_Rev5_V1.06.xlsx',
    },
    {
        key: 'govramp-ocm-moderate', version: 'V1.04', current: false, bytes: 464_757,
        sha256: 'b3e4e389214f0802c0abd737ad8edd0435db0b9aacb35a0eb79fa95b2606901c',
        filename: 'GovRAMP-Controls-Matrix_Mod_Rev5_V1.04_20250421.xlsx',
        source: 'GovRAMP Moderate Impact SP Package 2025-04-30 (1_OCM)',
    },
    {
        key: 'govramp-conmon-matrix', version: 'V1.2', current: true, bytes: 11_989_426,
        sha256: '67234b81123ed9a888e9924b1d7abdabf983361535e91098bcf592911cefdeaf',
        filename: 'GovRAMP-Continuous Monitoring-Matrix_Rev5_V1.2.xlsx',
        source: 'GovRAMP Moderate Impact SP Package 2025-04-30 (8_Continuous Monitoring Matrix)',
    },
]);
/** Largest template accepted on upload (the ConMon matrix is ~12 MB zipped). */
export const GOVRAMP_TEMPLATE_MAX_BYTES = 24 * 1024 * 1024;
export function govrampTemplateObjectKey(sha256) {
    if (!/^[0-9a-f]{64}$/.test(sha256))
        throw new Error('bad template sha256');
    return `templates/govramp/${sha256}.xlsx`;
}
export function currentGovrampTemplate(key) {
    return GOVRAMP_TEMPLATE_PINS.find((p) => p.key === key && p.current);
}
export function govrampTemplatePinBySha(sha256) {
    return GOVRAMP_TEMPLATE_PINS.find((p) => p.sha256 === sha256) ?? null;
}
/**
 * 800-53 control ids as the workbook writes them ("AC-2 (1)", "AC-02 (01)")
 * and as the OSCAL catalog does ("ac-2.1"), reduced to one key.
 */
export function normalizeControlId(raw) {
    return raw.toLowerCase().replace(/\s+/g, '')
        .replace(/\((\d+)\)/g, '.$1')
        .replace(/\d+/g, (d) => String(Number(d)));
}
// ---- Controls Matrix (OCM) --------------------------------------------------------------------
export const OCM_SHEETS = {
    informationSystem: '3_Information System',
    systemDescription: '4_System Description',
    ports: '7_Ports_Protocols_Services',
    interconnections: '8_Interconnections',
    cis: '9_CIS Matrix',
    crm: '10_CRM',
    inventory: '11_Inventory Workbook',
    controls: '12_Mod Controls',
};
/** The 23 inventory columns in workbook order (ADR-0066 §8), shared by the OCM and the ConMon matrix. */
export const INVENTORY_COLUMNS = {
    unique_asset_identifier: { header: 'UNIQUE ASSET IDENTIFIER' },
    ip_address: { header: 'IPv4 or IPv6 Address' },
    virtual: { header: 'Virtual' },
    public: { header: 'Public' },
    dns_name_or_url: { header: 'DNS Name or URL' },
    netbios_name: { header: 'NetBIOS Name' },
    mac_address: { header: 'MAC Address' },
    authenticated_scan: { header: 'Authenticated Scan' },
    baseline_configuration_name: { header: 'Baseline Configuration Name' },
    os_name_and_version: { header: 'OS Name and Version' },
    location: { header: 'Location' },
    asset_type: { header: 'Asset Type' },
    hardware_make_model: { header: 'Hardware Make/Model' },
    in_latest_scan: { header: 'In Latest Scan' },
    software_database_vendor: { header: 'Software/ Database Vendor' },
    software_database_name_and_version: { header: 'Software/ Database Name & Version' },
    patch_level: { header: 'Patch Level' },
    function: { header: 'Function' },
    comments: { header: 'Comments' },
    serial_asset_tag: { header: 'Serial #/Asset Tag#' },
    vlan_network_id: { header: 'VLAN/ Network ID' },
    system_owner: { header: 'System Administrator/ Owner' },
    application_owner: { header: 'Application Administrator/ Owner' },
};
export const INVENTORY_FIELD_ORDER = Object.keys(INVENTORY_COLUMNS);
export const OCM_INVENTORY = { sheet: OCM_SHEETS.inventory, headerRows: [3], firstDataRow: 5, columns: INVENTORY_COLUMNS };
export const PORT_COLUMNS = {
    port: { header: 'Port Number' },
    transport: { header: 'TCP/UDP', list: true },
    service: { header: 'Services' },
    purpose: { header: 'Purpose' },
    used_by: { header: 'Used By' },
};
export const OCM_PORTS = { sheet: OCM_SHEETS.ports, headerRows: [3], firstDataRow: 4, columns: PORT_COLUMNS };
export const INTERCONNECTION_COLUMNS = {
    leveraged_sp_name: { header: 'Leveraged SP Name' },
    leveraged_information_system: { header: 'Leveraged Information System' },
    connection_details: { header: 'Connection Details' },
    hosting_environment: { header: 'Hosting Environment' },
    data_types: { header: 'Data Types' },
    information_transmitted: { header: 'Information Being Transmitted' },
    ports_or_circuits: { header: 'Port or Circuit Numbers' },
    authorization_id: { header: 'GovRAMP/FedRAMP ID' },
    assessment_type: { header: 'Assessment Type', list: true },
    impact_level: { header: 'Impact Level', list: true },
};
/** Row 3 holds GovRAMP's guidance text; data starts at row 4. */
export const OCM_INTERCONNECTIONS = { sheet: OCM_SHEETS.interconnections, headerRows: [2], firstDataRow: 4, columns: INTERCONNECTION_COLUMNS };
export const CONTROLS_COLUMNS = {
    control_provider: { header: 'Control Provider', list: true },
    implementation_status: { header: 'Implementation Status', list: true },
    fully_inherited: { header: 'Fully Inherited from Service Provider', list: true },
    sp_implementation: { header: 'Service Provider Implementation' },
    control_completed: { header: 'Control Completed', list: true },
};
/** Header labels sit on rows 1 (customer columns) and 2 (GovRAMP's columns); the 319 controls start at row 3. */
export const OCM_CONTROLS = { sheet: OCM_SHEETS.controls, headerRows: [1, 2], firstDataRow: 3, keyHeader: 'ID', columns: CONTROLS_COLUMNS };
export const CRM_COLUMNS = {
    inherited: { header: 'Inherited from SP', list: true },
    guidance: { header: 'SP Guidance' },
    services: { header: 'SP Services' },
    iaas: { header: 'IaaS', list: true },
    paas: { header: 'PaaS', list: true },
    saas: { header: 'SaaS', list: true },
};
export const OCM_CRM = { sheet: OCM_SHEETS.crm, headerRows: [3], firstDataRow: 4, keyHeader: 'SORT ID', columns: CRM_COLUMNS };
const B = { column: 'B' };
const contact = (prefix, section) => [
    ['name', 'Name'], ['title', 'Title'], ['organization', 'Company/Organization'], ['address', 'Address'], ['phone', 'Phone Number'], ['email', 'Email Address'],
].map(([k, label]) => ({ id: `${prefix}_${k}`, path: [section, label], target: B }));
const org = (prefix, section) => [
    ['organization_name', 'Organization Name'], ['street_address', 'Street Address'], ['suite', 'Suite/Room/Building'], ['city_state_zip', 'City, State, Zip'],
].map(([k, label]) => ({ id: `${prefix}_${k}`, path: [section, label], target: B }));
export const INFORMATION_SYSTEM_FIELDS = [
    { id: 'service_provider_name', path: ['Service Provider Name'], target: B },
    { id: 'information_system_name', path: ['Information System Name'], target: B },
    { id: 'version', path: ['Version'], target: B },
    { id: 'date', path: ['Date'], target: B },
    { id: 'impact_level', path: ['Impact Level'], target: B, list: true },
    { id: 'operational_status', path: ['Operational Status'], target: B, list: true },
    { id: 'cloud_service_model', path: ['Cloud Service Model'], target: B, list: true },
    { id: 'cloud_deployment_model', path: ['Cloud Deployment Model'], target: B, list: true },
    { id: 'digital_identity_level', path: ['Digital Identity Level Selection'], target: B, list: true },
    { id: 'authorization_path', path: ['Authorization Path'], target: B, list: true },
    ...org('csp', 'Identification of Cloud Service Provider'),
    ...contact('owner', 'Information System Owner'),
    ...contact('authorizing_official', 'Internal Authorizing Official'),
    ...contact('security_contact', 'Security Responsibility'),
    ...org('preparer', 'Organization that Prepared this Document'),
];
export const SYSTEM_DESCRIPTION_FIELD = {
    id: 'general_system_description', path: ['General System Description'], match: 'prefix', target: { below: 1 },
};
// ---- ConMon matrix -----------------------------------------------------------------------------
export const CONMON_SHEETS = { openPoam: 'Open POA&M', closedPoam: 'Closed POA&M', inventory: 'Inventory Workbook' };
const poamColumns = (lists) => ({
    poam_id: { header: 'POAM ID' },
    controls: { header: 'Controls' },
    weakness_name: { header: 'Weakness Name' },
    weakness_description: { header: 'Weakness Description' },
    detector_source: { header: 'Weakness Detector Source' },
    source_identifier: { header: 'Weakness Source Identifier' },
    asset_identifier: { header: 'Asset Identifier' },
    point_of_contact: { header: 'Point of Contact' },
    resources_required: { header: 'Resources Required' },
    remediation_plan: { header: 'Overall Remediation Plan' },
    detection_date: { header: 'Original Detection Date' },
    // Open POA&M computes this from detection date + risk (GovRAMP's own formula); keep it where present.
    scheduled_completion: { header: 'Scheduled Completion Date', onFormula: 'skip' },
    milestones: { header: 'Planned Milestones' },
    milestone_changes: { header: 'Milestone Changes' },
    vendor_dependency: { header: 'Vendor Dependency', list: lists },
    original_risk: { header: 'Original Risk Rating', list: lists },
    adjusted_risk: { header: 'Adjusted Risk Rating', list: lists },
    false_positive: { header: 'False Positive', list: lists },
    operational_requirement: { header: 'Operational Requirement', list: lists },
    deviation_rationale: { header: 'Deviation Rationale' },
    supporting_documents: { header: 'Supporting Documents' },
    comments: { header: 'Comments' },
});
/** Open POA&M: header row 3, rows from 4 (row 5 down carry GovRAMP's due-date formula). */
export const CONMON_OPEN_POAM = {
    sheet: CONMON_SHEETS.openPoam, headerRows: [3], firstDataRow: 4,
    columns: { ...poamColumns(true), status_date: { header: 'Status Date' } },
};
/** Closed POA&M: the template's dropdowns there are anchored at row 1 only, so values are not list-checked on this sheet. */
export const CONMON_CLOSED_POAM = {
    sheet: CONMON_SHEETS.closedPoam, headerRows: [3], firstDataRow: 4,
    columns: { ...poamColumns(false), closed_date: { header: 'Closed Date' } },
};
/** The ConMon matrix inventory: header row 2, guidance row 3, data from row 4. */
export const CONMON_INVENTORY = { sheet: CONMON_SHEETS.inventory, headerRows: [2], firstDataRow: 4, columns: INVENTORY_COLUMNS };
//# sourceMappingURL=govramp-templates.js.map